import InvoicePreview from '../Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlink } from '@fortawesome/free-solid-svg-icons';
import InputAutocomplete from '../input-autocomplete/input-autocomplete';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { inject } from 'mobx-react';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './invoice-preview-select.scss';

const InvoicePreviewSelect = ({ data, transaction, statement, updateTransaction, matchingExpenses, removeExpense, additionalExpenseOptions }) => {
  const expenses = [
    ...(statement.expenses || []),
    ...(additionalExpenseOptions && Array.isArray(additionalExpenseOptions) ? additionalExpenseOptions : []),
  ];

  // Keep track of previously matched transaction so we don't flash a spinner if processing new receipts
  const lastMatchedRef = useRef(null);

  useEffect(() => {
    if (transaction?.checksum) {
      lastMatchedRef.current = {
        checksum: transaction.checksum,
        file_name: transaction.file_name ? transaction.file_name : `${statement._id}_${transaction.checksum}.pdf`,
        name: transaction.name,
      };
    }
  }, [transaction?.checksum, transaction?.file_name, transaction?.name, statement?._id]);

  const handleChange = (choice) => {
    if (!choice) return;
    const selectedExpense = statement.expenses?.find((xp) => xp.checksum === choice.value);
    if (selectedExpense) {
      updateTransaction({
        ...transaction,
        json: selectedExpense.json,
        checksum: choice.value,
        name: choice.value,
      });
    }

    const selectedCategory = additionalExpenseOptions?.find((xp) => xp.checksum === choice.value);
    if (selectedCategory) {
      const supplier = selectedCategory.supplier;
      const taxesPercentage = data.config?.tax_profile?.reduce((acc, t) => {
        acc[t.code] = t.adjusted_rates ?? t.list_rates;
        return acc;
      }, {});
      const listItems = [
        {
          IsBillable: supplier?.IsBillable,
          Total: transaction.amount,
          Tax1: 0,
          Tax2: 0,
          GLNatural: supplier?.gl_code,
          CostType: supplier?.cost_type || '',
          // gl_office is the GL-office space, office is the office space (XPEN-3115).
          GLOffice: supplier?.gl_office || supplier?.office,
          Office: supplier?.office,
          Description: selectedCategory.name,
        },
      ];

      const InvoiceDate = new Date(transaction.date).toISOString().split('T')[0];
      const DueDate =
        Number.isInteger(supplier.payment_terms) && InvoiceDate
          ? new Date(new Date(InvoiceDate).setDate(new Date(InvoiceDate).getDate() + supplier.payment_terms)).toISOString().split('T')[0]
          : '';
      const json = {
        from_category: true,
        Status: 'Completed',
        InvoiceDate: InvoiceDate,
        DueDate: DueDate,
        GLNatural: supplier?.gl_code,
        CostType: supplier?.cost_type || '',
        IsBillable: supplier?.IsBillable,
        Office: supplier?.office,
        // No office fallback here: the header is GLValue-space and the export
        // reads it raw, so an office code would skip the Office -> GLValue step.
        GLOffice: supplier?.gl_office,
        Currency: statement.currency_code, 
          Category: selectedCategory.name,
          Name: selectedCategory.name,
        ListItem: listItems,
        Tax1: 0,
        Tax2: 0,
        Total: transaction.amount,
        Tax: 0,
        TotalWithTax: transaction.amount,
        BigTotalPayed: transaction.amount,
        ListSupplier: [
          {
            Name: supplier?.names[0],
            Number: supplier?.supplier_number,
            Index: supplier?._id,
            GLNatural: supplier?.gl_code,
            CostType: supplier?.cost_type || '',
            IsBillable: supplier?.IsBillable,
            payment_terms: supplier?.payment_terms,
          },
        ],
      };
      updateTransaction({
        ...transaction,
        invoice_id: null,
        json: json,
        status: 'Completed',
        checksum: choice.value,
      });
    }
  };

  return transaction.checksum || transaction.json?.from_category ? (
    <div className='d-flex align-items-center justify-content-between' data-testid='invoice-preview-container'>
      {transaction.json?.from_category ? (
        <div className='text-truncate'>{transaction.json?.Name}</div>
      ) : (
        <InvoicePreview
          file_name={transaction.json?.Name || `${statement._id}_${transaction.checksum}.pdf`}
          currentInvoice={null}
          data={data}
          data-testid='invoice-preview'
        />
      )}
      {removeExpense && (
        <div className='ml-5'>
          {(!transaction.submitted || transaction.json?.invoice_id) && (
            <FontAwesomeIcon
              icon={faUnlink}
              className='text-danger unlink-icon'
              onClick={(e) => {
                e.stopPropagation();
                removeExpense(transaction);
              }}
              aria-label='Remove expense'
              title='Remove expense'
              data-testid='remove-expense-button'
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <>
      {(() => {
        const hasProcessing =
          statement.expenses &&
          statement.expenses.length > 0 &&
          statement.expenses.some((expense) => ['Pending', 'Processing'].includes(expense.status));
        // If we previously had a matched receipt, keep showing it instead of spinner while processing new ones
        if (!transaction.checksum && hasProcessing && lastMatchedRef.current) {
          return (
            <div className='d-flex align-items-center justify-content-between' data-testid='invoice-preview-container-updating'>
              <InvoicePreview file_name={lastMatchedRef.current.file_name} currentInvoice={null} data={data} data-testid='invoice-preview-updating' />
              <span className='ml-2 small text-muted'>{data.t.read('cc_transactions.updating_receipt') || 'Updating receipt...'}</span>
            </div>
          );
        }
        if (hasProcessing) {
          return <LoadingSpinner data-testid='loading-spinner' />;
        }
        return (
          <InputAutocomplete
            options={expenses
              .filter((xp) => !matchingExpenses?.includes(xp.checksum))
              .map((xp) => ({ label: xp.name || xp.original_file_name, value: xp.checksum }))}
            onChange={handleChange}
            value={transaction.checksum && transaction.name ? { value: transaction.checksum, label: transaction.name } : null}
            className={'mb-0'}
            data-testid='expense-selector'
          />
        );
      })()}
    </>
  );
};

export default inject('data')(InvoicePreviewSelect);

InvoicePreviewSelect.propTypes = {
  data: PropTypes.shape({
    t: PropTypes.shape({
      read: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  transaction: PropTypes.shape({
    checksum: PropTypes.string,
    file_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }).isRequired,
  statement: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    expenses: PropTypes.arrayOf(
      PropTypes.shape({
        checksum: PropTypes.string.isRequired,
        status: PropTypes.string,
        json: PropTypes.object,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  updateTransaction: PropTypes.func.isRequired,
  matchingExpenses: PropTypes.arrayOf(PropTypes.string),
  removeExpense: PropTypes.func,
  additionalExpenseOptions: PropTypes.arrayOf(
    PropTypes.shape({
      checksum: PropTypes.string,
      name: PropTypes.string,
    })
  ),
};

InvoicePreviewSelect.defaultProps = {
  matchingExpenses: [],
  removeExpense: undefined,
  additionalExpenseOptions: [],
};
